import React, { ReactNode, useCallback } from "react";
import { connect } from "react-redux";

import { ColumnsType } from "antd/es/table/Table";
import { useIntl } from "react-intl";

import { Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import { Button, Modal } from "@mapmycustomers/ui";

import Table from "@app/component/Table";
import ReportType from "@app/enum/ReportType";
import downloadEntityPreviewsAsCsv from "@app/scene/reports/utils/csv/downloadEntityPreviewsAsCsv";
import { showEntityView } from "@app/store/entityView/actions";

import { DownloadConfirmation } from "./DownloadConfirmation";

interface Props {
  columns: ColumnsType<Company | Deal | Person>;
  onHide: () => void;
  onPageChange?: (page: number) => void;
  page?: number;
  pageSize?: number;
  records: Array<Company | Deal | Person>;
  showEntityView: typeof showEntityView;
  title: ReactNode;
  totalRecords?: number;
}

const PreviewListModal: React.FC<Props> = ({
  columns,
  onHide,
  onPageChange,
  page,
  pageSize,
  records,
  showEntityView,
  title,
  totalRecords,
}) => {
  const intl = useIntl();

  const rowEventHandlers = useCallback(
    (entity: Company | Deal | Person) => ({
      onClick: () => showEntityView({ entityId: entity.id, entityType: entity.entity }),
    }),
    [showEntityView]
  );

  const handleDownload = useCallback(() => {
    const accountsRecords: Company[] = [];
    const contactsRecords: Person[] = [];
    const dealsRecords: Deal[] = [];
    records.forEach((record) => {
      if (record.entity === EntityType.COMPANY) {
        accountsRecords.push(record as Company);
      } else if (record.entity === EntityType.PERSON) {
        contactsRecords.push(record as Person);
      } else if (record.entity === EntityType.DEAL) {
        dealsRecords.push(record as Deal);
      }
    });
    if (accountsRecords.length) {
      downloadEntityPreviewsAsCsv(accountsRecords, ReportType.COMPANIES);
    }
    if (contactsRecords.length) {
      downloadEntityPreviewsAsCsv(contactsRecords, ReportType.PEOPLE);
    }
    if (dealsRecords.length) {
      downloadEntityPreviewsAsCsv(dealsRecords, ReportType.DEALS);
    }
  }, [records]);

  const handleChangeTable = useCallback(
    (pagination) => {
      onPageChange?.(pagination.current);
    },
    [onPageChange]
  );

  return (
    <Modal
      destroyOnClose
      footer={[
        <DownloadConfirmation key="download" onConfirm={handleDownload}>
          <Button type="primary">
            {intl.formatMessage({
              id: "previewListModal.downloadRecordsButton",
              defaultMessage: "Quick export all records",
              description: "Download all records button on the list modal",
            })}
          </Button>
        </DownloadConfirmation>,
      ]}
      onCancel={onHide}
      open
      title={title}
      width="clamp(300px, 70vw, 780px)"
    >
      <Table<Company | Deal | Person>
        clickableRows
        columns={columns}
        dataSource={records}
        noHover
        onChange={handleChangeTable}
        onRow={rowEventHandlers}
        pagination={{
          current: page,
          pageSize: pageSize,
          position: ["bottomCenter"],
          showSizeChanger: false,
          total: totalRecords,
        }}
        rowKey="id"
        scroll={{ y: 335 }}
      />
    </Modal>
  );
};

const mapDispatchToProps = {
  showEntityView,
};

export default connect(null, mapDispatchToProps)(PreviewListModal);
